import React from 'react';
import { DashboardMetricTone, DashboardOverview } from '../../types/dashboard';
import { formatNumber } from '../../lib/dashboard/mapOverview';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useThemeColor } from '../../context/BrandingContext';

interface AgentRealtimeSectionProps {
  overview: DashboardOverview;
}

const toneClassMap: Record<DashboardMetricTone, string> = {
  positive: 'bg-green-500',
  primary: 'bg-blue-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
};

const CAPABILITY_LABEL_OVERRIDES: Record<string, string> = {};

function humanizeCapabilityCode(code: string): string {
  if (CAPABILITY_LABEL_OVERRIDES[code]) return CAPABILITY_LABEL_OVERRIDES[code];
  return code
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanizeChannel(channel: string): string {
  return channel
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export const AgentRealtimeSection: React.FC<AgentRealtimeSectionProps> = ({ overview }) => {
  // Chart props need real colours, not classes; this follows the theme.
  const themeColor = useThemeColor();
  const chartData = overview.trend.map((point) => ({ label: point.label, value: point.conversations }));
  const totalConversationsInTrend = overview.trend.reduce((sum, p) => sum + p.conversations, 0);
  const busiestDay = [...overview.trend].sort((a, b) => b.conversations - a.conversations)[0];
  const successRate = overview.kpis.workflowSuccessRate.value;

  const maxIntentCount = Math.max(1, ...overview.topIntents.map((i) => i.count));
  const totalChannelCount = overview.channelMix.reduce((sum, c) => sum + c.count, 0);

  return (
    <section className="mt-12 sm:mt-16 lg:mt-20">
      <div className="mt-4 grid grid-cols-1 gap-5 sm:mt-5 lg:grid-cols-12 xl:mx-auto xl:max-w-[1466px]">
        <article className="min-w-0 rounded-card bg-white p-4 shadow-[0_20px_45px_rgb(var(--c-1c2350,28_35_80)/0.08)] sm:p-5 lg:col-span-7 lg:p-6">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-title-md font-semibold text-c-1a1b2f sm:text-title-lg">Conversation Trend</h3>
              <p className="mt-2 text-4xl font-semibold leading-none text-c-5a67e6 sm:mt-3 sm:text-5xl">
                {formatNumber(totalConversationsInTrend)}
              </p>
              <p className="text-sm text-c-747a9d">conversations in the last 7 days</p>
            </div>
            <span className="flex-shrink-0 rounded-[10px] border border-c-d5ddfb px-3 py-2 text-xs font-medium text-c-3e4f99">
              7 Days
            </span>
          </div>

          <div className="mt-4 h-[176px] min-w-0 sm:h-[208px] md:h-[224px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 8, left: 8, bottom: 4 }}>
                <defs>
                  <linearGradient id="taskTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={themeColor('#7d8df7')} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={themeColor('#7d8df7')} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={themeColor('#edf0fb')} vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: themeColor('#8b90af'), fontSize: 12 }} />
                <YAxis hide allowDecimals={false} />
                <Area
                  type="linear"
                  dataKey="value"
                  stroke={themeColor('#6071ec')}
                  strokeWidth={3}
                  fill="url(#taskTrendFill)"
                  dot={{ r: 4.5, fill: themeColor('#6071ec'), strokeWidth: 0 }}
                  activeDot={{ r: 5.5, fill: themeColor('#6071ec'), strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <p className="mt-3 text-sm font-medium text-c-4d58a8">
            {busiestDay && busiestDay.conversations > 0
              ? `${formatNumber(busiestDay.conversations)} conversations on ${busiestDay.label}, the busiest day this week`
              : 'No conversations recorded yet this week'}
          </p>
        </article>

        <article className="relative isolate min-w-0 overflow-hidden rounded-card bg-[radial-gradient(circle_at_50%_20%,rgb(var(--c-14218a,20_33_138)),rgb(var(--c-05062b,5_6_43))_55%,rgb(var(--c-040624,4_6_36))_100%)] p-4 text-white shadow-[0_20px_45px_rgb(var(--c-0c1446,12_20_70)/0.45)] sm:p-5 lg:col-span-5 lg:p-6">
          <div className="relative z-10 mx-auto mt-3 flex h-[184px] w-full max-w-[200px] items-center justify-center rounded-full border border-white/20 bg-[radial-gradient(circle_at_50%_45%,rgba(76,236,218,0.18),rgb(var(--c-040624,4_6_36)/0.2)_60%)] sm:mt-4 sm:h-[224px] sm:max-w-[224px]">
            <div className="flex h-[130px] w-[130px] items-center justify-center rounded-full border-4 border-[#75f5ec] bg-c-07103b sm:h-[155px] sm:w-[155px]">
              <div className="px-2 text-center">
                <p className="whitespace-nowrap text-3xl font-semibold leading-none tracking-tight sm:text-4xl">
                  {successRate === null ? '—' : `${successRate.toFixed(1)}%`}
                </p>
                <p className="mt-1.5 text-xs text-white/80 sm:text-sm">Workflow Success</p>
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-3 grid grid-cols-2 gap-2.5 text-center text-xs">
            <div className="rounded-xl bg-white/10 p-2.5">
              <p className="text-white/65">Human Handoff Rate</p>
              <p className="mt-1 text-base font-semibold">{overview.handoffRate}%</p>
            </div>
            <div className="rounded-xl bg-white/10 p-2.5">
              <p className="text-white/65">Knowledge Base</p>
              <p className="mt-1 text-base font-semibold">{formatNumber(overview.knowledgeBase.qaPairCount)} articles</p>
            </div>
          </div>
        </article>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-12 xl:mx-auto xl:max-w-[1466px]">
        <article className="min-w-0 rounded-card bg-white p-4 shadow-[0_20px_45px_rgb(var(--c-1c2350,28_35_80)/0.08)] sm:p-5 lg:col-span-4 lg:p-6">
          <h3 className="text-title-md font-semibold text-c-1a1b2f sm:text-title-lg">Response Time</h3>
          <p className="text-sm text-c-747a9d">
            {overview.responseTimeBuckets.total > 0
              ? `Based on ${formatNumber(overview.responseTimeBuckets.total)} agent replies`
              : 'No timed replies recorded yet'}
          </p>

          <div className="mt-5 space-y-2.5">
            {overview.responseTimeBuckets.buckets.map((bucket) => (
              <div key={bucket.id} className="grid grid-cols-[46px_1fr_34px] items-center gap-3 text-sm">
                <span className="text-c-374067">{bucket.label}</span>
                <div className="h-2 rounded-full bg-c-eef2fd">
                  <div className={`h-2 rounded-full ${toneClassMap[bucket.tone]}`} style={{ width: `${bucket.pct}%` }} />
                </div>
                <span className="text-right font-semibold text-c-1f2447">{bucket.pct}%</span>
              </div>
            ))}
          </div>
        </article>

        <article className="min-w-0 rounded-card bg-white p-4 shadow-[0_20px_45px_rgb(var(--c-1c2350,28_35_80)/0.08)] sm:p-5 lg:col-span-8 lg:p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="min-w-0">
              <h3 className="text-title-md font-semibold text-c-1a1b2f sm:text-title-lg">What people are asking</h3>
              <p className="mt-1 text-sm text-c-747a9d">Top intents detected across conversations</p>
              <div className="mt-4 space-y-3">
                {overview.topIntents.length === 0 ? (
                  <p className="text-sm text-c-8f95b4">No intents detected yet.</p>
                ) : (
                  overview.topIntents.map((intent) => (
                    <div key={intent.capabilityCode}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="truncate text-c-374067">{humanizeCapabilityCode(intent.capabilityCode)}</span>
                        <span className="font-semibold text-c-1f2447">{formatNumber(intent.count)}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-c-eef2fd">
                        <div
                          className="h-1.5 rounded-full bg-c-6071ec"
                          style={{ width: `${(intent.count / maxIntentCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="min-w-0">
              <h3 className="text-title-md font-semibold text-c-1a1b2f sm:text-title-lg">Channel mix</h3>
              <p className="mt-1 text-sm text-c-747a9d">Where conversations are happening</p>
              <div className="mt-4 space-y-3">
                {overview.channelMix.length === 0 ? (
                  <p className="text-sm text-c-8f95b4">No channel activity yet.</p>
                ) : (
                  overview.channelMix.map((channel) => {
                    const pct = totalChannelCount ? Math.round((channel.count / totalChannelCount) * 100) : 0;
                    return (
                      <div key={channel.channel}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate text-c-374067">{humanizeChannel(channel.channel)}</span>
                          <span className="font-semibold text-c-1f2447">{pct}%</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-c-eef2fd">
                          <div className="h-1.5 rounded-full bg-c-7f54dc" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};
